import * as yup from 'yup';
import i18n from 'helpers/i18n';
import messages from './messages';
import { FormikHelpers, FormikProps } from 'formik';
import { useRef, useState } from 'react';
import { pickUserFields } from './utils';
import useAsync from 'hooks/useAsync';
import { useDispatch } from 'react-redux';
import { update } from 'store/slices/user';
import { updateUserMe } from 'actions/userMe';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  Button,
  Text,
  TextField,
  Select,
  Combobox
} from '@teachstonellc/component-library';
import { useStates } from 'components/dropdowns/States';
import { toastSuccess, toastError } from 'components/Toast';
import { useCountries } from 'components/dropdowns/Countries';
import { useLanguages } from 'components/dropdowns/Languages';
import { useEmailNotifications } from 'components/dropdowns/EmailNotifications';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';

import { Formik, PreventNavigation } from 'components/EnhancedForm';

import {
  Section,
  SectionTitle,
  FieldRow,
  Actions,
  ButtonWrapper
} from './Styled';

const validationSchema = yup.object({
  first_name: yup
    .string()
    .required(i18n.ft(messages.errors.firstName.required)),
  last_name: yup.string().required(i18n.ft(messages.errors.lastName.required)),
  email: yup
    .string()
    .email(i18n.ft(messages.errors.email.invalid))
    .required(i18n.ft(messages.errors.email.required)),
  email_notification_preference: yup
    .string()
    .nullable()
    .required(i18n.ft(messages.errors.notifications.required)),
  preferred_language: yup
    .string()
    .nullable()
    .required(i18n.ft(messages.errors.language.required)),
  street_address_1: yup.string().nullable(),
  state_registry_id: yup.string().nullable(),
  street_address_2: yup.string().nullable(),
  locality: yup.string().nullable(),
  country: yup.string().nullable(),
  region: yup.string().nullable(),
  postal_code: yup.string().nullable()
});

function isInvalid(formik: FormikProps<any>, field: string) {
  return Boolean(formik.touched[field] && formik.errors[field]);
}

function AccountPage() {
  const states = useStates();
  const countries = useCountries();
  const languages = useLanguages();
  const notifications = useEmailNotifications();
  const currentUser = useCurrentUser();
  const { run, isLoading } = useAsync();
  const dispatch = useDispatch();

  const countryOptions = countries.map(c => ({
    value: c.value,
    label: c.text
  }));
  const stateOptions = states.map(s => ({ value: s.value, label: s.text }));

  const [countryQuery, setCountryQuery] = useState('');
  const [stateQuery, setStateQuery] = useState('');

  const firstNameRef = useRef<HTMLInputElement>(null);
  const lastNameRef = useRef<HTMLInputElement>(null);
  const emailRef = useRef<HTMLInputElement>(null);
  // `Select` doesn't forward a ref, so these point at its wrapper and
  // `FocusFirstInvalidField` focuses the trigger button inside.
  const notificationsRef = useRef<HTMLDivElement>(null);
  const languageRef = useRef<HTMLDivElement>(null);

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    run(updateUserMe(values), {
      onSuccess: (data: any) => {
        dispatch(update(data));
        formik.resetForm({ values: values });
        toastSuccess();
      },
      onError: (error: any) => {
        if (error.errors?.user?.email) {
          const message = error.errors.user.email;
          formik.setFieldError('email', message);
          toastError({
            description: i18n.ft(messages.errors.base, { extra: message })
          });
        } else {
          toastError();
        }
        formik.setSubmitting(false);
      }
    });
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={validationSchema}
      initialValues={pickUserFields(currentUser)}
    >
      {formik => (
        <form
          noValidate
          onSubmit={formik.handleSubmit}
          onReset={formik.handleReset}
        >
          <FocusFirstInvalidField
            submitCount={formik.submitCount}
            isPending={formik.isSubmitting}
            fields={[
              {
                isInvalid: isInvalid(formik, 'first_name'),
                inputRef: firstNameRef
              },
              {
                isInvalid: isInvalid(formik, 'last_name'),
                inputRef: lastNameRef
              },
              { isInvalid: isInvalid(formik, 'email'), inputRef: emailRef },
              {
                isInvalid: isInvalid(formik, 'email_notification_preference'),
                inputRef: notificationsRef
              },
              {
                isInvalid: isInvalid(formik, 'preferred_language'),
                inputRef: languageRef
              }
            ]}
          />
          <Text variant="p-sm">{i18n.ft(messages.requiredFieldsNote)}</Text>

          <Section>
            <SectionTitle>{i18n.ft(messages.information)}</SectionTitle>
            <PreventNavigation />
            <FieldRow>
              <TextField
                required
                aria-required
                label={i18n.ft(messages.fields.firstName)}
                {...formik.getFieldProps('first_name')}
                ref={firstNameRef}
                error={
                  formik.touched.first_name
                    ? formik.errors.first_name
                    : undefined
                }
              />
              <TextField
                required
                aria-required
                label={i18n.ft(messages.fields.lastName)}
                {...formik.getFieldProps('last_name')}
                ref={lastNameRef}
                error={
                  formik.touched.last_name ? formik.errors.last_name : undefined
                }
              />
              <TextField
                type="email"
                required
                aria-required
                label={i18n.ft(messages.fields.email)}
                {...formik.getFieldProps('email')}
                ref={emailRef}
                error={formik.touched.email ? formik.errors.email : undefined}
              />
              <TextField
                label={i18n.ft(messages.fields.stateRegistry)}
                {...formik.getFieldProps('state_registry_id')}
                error={
                  formik.touched.state_registry_id
                    ? formik.errors.state_registry_id
                    : undefined
                }
              />
            </FieldRow>
          </Section>

          <Section>
            <SectionTitle>{i18n.ft(messages.settings)}</SectionTitle>

            <FieldRow>
              <div
                ref={notificationsRef}
                onBlur={() =>
                  formik.setFieldTouched('email_notification_preference', true)
                }
              >
                <Select
                  required
                  label={i18n.ft(messages.fields.notifications)}
                  options={notifications}
                  value={formik.values.email_notification_preference || null}
                  onChange={value =>
                    formik.setFieldValue('email_notification_preference', value)
                  }
                  error={
                    formik.touched.email_notification_preference
                      ? formik.errors.email_notification_preference
                      : undefined
                  }
                />
              </div>
              <div
                ref={languageRef}
                onBlur={() =>
                  formik.setFieldTouched('preferred_language', true)
                }
              >
                <Select
                  required
                  label={i18n.ft(messages.fields.language)}
                  options={languages}
                  value={formik.values.preferred_language || null}
                  onChange={value =>
                    formik.setFieldValue('preferred_language', value)
                  }
                  error={
                    formik.touched.preferred_language
                      ? formik.errors.preferred_language
                      : undefined
                  }
                />
              </div>
            </FieldRow>
          </Section>

          <Section>
            <SectionTitle>{i18n.ft(messages.address)}</SectionTitle>

            <FieldRow>
              <TextField
                label={i18n.ft(messages.fields.shipping1)}
                placeholder={i18n.ft(messages.fields.shipping1Placeholder)}
                {...formik.getFieldProps('street_address_1')}
                error={
                  formik.touched.street_address_1
                    ? formik.errors.street_address_1
                    : undefined
                }
              />
              <TextField
                label={i18n.ft(messages.fields.shipping2)}
                placeholder={i18n.ft(messages.fields.shipping2Placeholder)}
                {...formik.getFieldProps('street_address_2')}
                error={
                  formik.touched.street_address_2
                    ? formik.errors.street_address_2
                    : undefined
                }
              />
              <TextField
                label={i18n.ft(messages.fields.city)}
                placeholder={i18n.ft(messages.fields.city)}
                {...formik.getFieldProps('locality')}
                error={
                  formik.touched.locality ? formik.errors.locality : undefined
                }
              />

              <div onBlur={() => formik.setFieldTouched('country', true)}>
                <Combobox
                  label={i18n.ft(messages.fields.country)}
                  options={countryOptions}
                  selectedOption={
                    countryOptions.find(
                      o => o.value === formik.values.country
                    ) ?? null
                  }
                  onChange={option => {
                    formik.setFieldValue('country', option?.value ?? null);
                    setCountryQuery(option?.label ?? '');
                  }}
                  query={countryQuery}
                  setQuery={setCountryQuery}
                  showDropdownIcon
                  showSearchIcon={false}
                  immediate={false}
                />
              </div>

              {formik.values.country === 'US' ? (
                <div onBlur={() => formik.setFieldTouched('region', true)}>
                  <Combobox
                    label={i18n.ft(messages.fields.state)}
                    placeholder={i18n.ft(messages.fields.stateSearch)}
                    options={stateOptions}
                    selectedOption={
                      stateOptions.find(
                        o => o.value === formik.values.region
                      ) ?? null
                    }
                    onChange={option => {
                      formik.setFieldValue('region', option?.value ?? null);
                      setStateQuery(option?.label ?? '');
                    }}
                    query={stateQuery}
                    setQuery={setStateQuery}
                    showDropdownIcon
                    showSearchIcon={false}
                    immediate={false}
                  />
                </div>
              ) : (
                <TextField
                  label={i18n.ft(messages.fields.state)}
                  placeholder={i18n.ft(messages.fields.state)}
                  {...formik.getFieldProps('region')}
                  error={
                    formik.touched.region ? formik.errors.region : undefined
                  }
                />
              )}

              <TextField
                label={i18n.ft(messages.fields.postalCode)}
                placeholder={i18n.ft(messages.fields.postalCode)}
                {...formik.getFieldProps('postal_code')}
                error={
                  formik.touched.postal_code
                    ? formik.errors.postal_code
                    : undefined
                }
              />
            </FieldRow>
          </Section>

          <Actions>
            <ButtonWrapper>
              <Button
                fullWidth
                type="submit"
                variant="primary"
                shape="rounded"
                loading={isLoading}
                disabled={isLoading}
              >
                {i18n.ft(messages.save)}
              </Button>
            </ButtonWrapper>
          </Actions>
        </form>
      )}
    </Formik>
  );
}

export default AccountPage;
